import { getPersistedValue } from 'shared/utils/persisted-values';

import { isBrowser } from 'utils/misc-utils';
import { Session } from 'utils/session';
import { getLogger } from 'utils/datadog-logger';

const logger = getLogger('telemetry-client');

type Options = Partial<{
  resetSessionOnSuccess: boolean;
  // Lets the request outlive the page, for events fired right before a same-tab navigation.
  keepalive: boolean;
}>;

type Data = {
  type: string;
  payload: Record<string, unknown>;
};

export type TelemetryClient = {
  post(data: Data, options?: Options): Promise<void>;
};

const noop = (): Promise<void> => Promise.resolve();

async function post(data: Data, options: Options = {}): Promise<void> {
  const session = Session.getInstance();
  const accessToken = getPersistedValue('access-token');

  const headers = {
    'Content-Type': 'application/json',
    Authorization: accessToken,
    ...session.header,
  };

  const body = JSON.stringify(data);

  try {
    const resp = await fetch('/telemetry', {
      method: 'POST',
      headers,
      body,
      ...(options.keepalive && { keepalive: true }),
    });

    if (resp.ok && options.resetSessionOnSuccess) {
      session.reset();
    }

    if (!resp.ok) {
      logger.warn('telemetry.client.post_non_ok', {
        event_type: data.type,
        status: resp.status,
      });
    }
  } catch (err) {
    logger.warn('telemetry.client.post_failed', {
      event_type: data.type,
      error: err instanceof Error ? err.message : 'unknown_error',
    });
  }
}

export const createTelemetryClient = (): TelemetryClient => ({
  post: isBrowser() ? post : noop,
});
